import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import CloudSyncRoundedIcon from '@mui/icons-material/CloudSyncRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FacilityBulkJobsPanel from './FacilityBulkJobsPanel';
import {
  bulkProvisionIncludesParent,
  estimateBulkProvisionLocations,
} from './utils/facility-lms.utils';

const SCOPE_NOTE =
  'This bulk action provisions parent locations and facility Canvas subaccounts only. It does not provision existing learners. Use Canvas Provisioning on each facility to provision learners and monitor per-facility jobs before switching delivery LMS.';

const ASYNC_NOTE =
  'Bulk provisioning runs as an async job (accounts.csv → Canvas SIS import). The per-facility Provision Facility button in Canvas Provisioning walks the parent chain synchronously — both target parent + facility subaccounts, not learners.';

/**
 * Confirm + jobs modal for fleet-wide facility bulk provisioning.
 * Replaces the inline bulk jobs panel above the facility grid.
 */
const FacilityBulkProvisionDialog = ({
  open,
  mode,
  selectedRows,
  submitting,
  jobs,
  jobsLoading,
  jobsError,
  jobsSubmitting,
  onClose,
  onConfirm,
  onRefreshJobs,
  onRetryJob,
  onRefreshFacilities,
}) => {
  const isConfirm = mode === 'confirm';
  const title = isConfirm ? 'Bulk provision to Canvas' : 'Bulk facility provisioning jobs';
  const locationEstimate = estimateBulkProvisionLocations(selectedRows);
  const { facilityCount, estimatedLocationCount, withUnprovisionedParent } = locationEstimate;
  const showLocationEstimate = facilityCount > 0 && estimatedLocationCount > facilityCount;

  const selectionHeading = (() => {
    if (facilityCount === 0) return 'Selected facilities (0)';
    if (showLocationEstimate) {
      return `${facilityCount} facilit${facilityCount === 1 ? 'y' : 'ies'} selected · ~${estimatedLocationCount} Canvas locations estimated`;
    }
    return `Selected facilities (${facilityCount})`;
  })();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="facility-lms-bulk-provision-dialog"
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <DialogTitle
        sx={{
          p: 0,
          position: 'relative',
          background: 'linear-gradient(135deg, #3B1E8F 0%, #5B3FD9 100%)',
          color: '#FFFFFF',
        }}
      >
        <Box
          component="span"
          sx={{
            position: 'absolute',
            width: 1,
            height: 1,
            p: 0,
            m: -1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {title}
        </Box>
        <Box sx={{ px: { xs: 3, sm: 4 }, pt: { xs: 2.5, sm: 3 }, pb: { xs: 2.5, sm: 3 }, pr: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
            <CloudSyncRoundedIcon sx={{ fontSize: 16, opacity: 0.85 }} />
            <Typography
              variant="caption"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                fontWeight: 700,
                fontSize: '0.75rem',
                color: 'rgba(255,255,255,0.85)',
              }}
            >
              Bulk Canvas Provisioning
            </Typography>
          </Box>
          <Typography variant="h5" component="div" sx={{ fontWeight: 700, lineHeight: 1.2, color: '#FFFFFF' }}>
            {title}
          </Typography>
        </Box>
        <IconButton
          aria-label="Close bulk provisioning dialog"
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: 'rgba(255,255,255,0.9)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
          }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {isConfirm ? (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              {SCOPE_NOTE}
            </Alert>
            <DialogContentText sx={{ mb: 1 }}>{ASYNC_NOTE}</DialogContentText>
            {withUnprovisionedParent > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {withUnprovisionedParent} selected facilit{withUnprovisionedParent === 1 ? 'y includes' : 'ies include'} an
                unprovisioned parent location. The job total may be higher than the number of facilities you selected.
              </Typography>
            )}
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              {selectionHeading}
            </Typography>
            {selectedRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No facilities selected.
              </Typography>
            ) : (
              <List dense sx={{ maxHeight: 240, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {selectedRows.map((row) => {
                  const includesParent = bulkProvisionIncludesParent(row);
                  const secondaryParts = [];
                  if (row.shortCode) secondaryParts.push(`Short code: ${row.shortCode}`);
                  if (includesParent) secondaryParts.push('+1 parent location will also be provisioned');

                  return (
                    <ListItem key={row.locationKey} disableGutters sx={{ px: 2 }}>
                      <ListItemText
                        primary={row.locationName || row.shortCode || `Facility ${row.locationKey}`}
                        secondary={secondaryParts.length > 0 ? secondaryParts.join(' · ') : null}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </>
        ) : (
          <FacilityBulkJobsPanel
            jobs={jobs}
            loading={jobsLoading}
            error={jobsError}
            submitting={jobsSubmitting}
            onRefresh={onRefreshJobs}
            onRetry={onRetryJob}
            onRefreshFacilities={onRefreshFacilities}
            embedded
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          {isConfirm ? 'Cancel' : 'Close'}
        </Button>
        {isConfirm && (
          <Button
            variant="contained"
            color="primary"
            onClick={onConfirm}
            disabled={submitting || selectedRows.length === 0}
            data-testid="facility-lms-bulk-provision-confirm"
          >
            {submitting ? 'Starting…' : 'Start bulk provisioning'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

FacilityBulkProvisionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['confirm', 'jobs']).isRequired,
  selectedRows: PropTypes.arrayOf(PropTypes.object),
  submitting: PropTypes.bool,
  jobs: PropTypes.arrayOf(PropTypes.object),
  jobsLoading: PropTypes.bool,
  jobsError: PropTypes.object,
  jobsSubmitting: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  onRefreshJobs: PropTypes.func,
  onRetryJob: PropTypes.func,
  onRefreshFacilities: PropTypes.func,
};

FacilityBulkProvisionDialog.defaultProps = {
  selectedRows: [],
  submitting: false,
  jobs: [],
  jobsLoading: false,
  jobsError: null,
  jobsSubmitting: false,
  onConfirm: undefined,
  onRefreshJobs: undefined,
  onRetryJob: undefined,
  onRefreshFacilities: undefined,
};

export default FacilityBulkProvisionDialog;
